/**
 * A WebSocket that comes back.
 *
 * The three sockets in this app — the worklist, the exam media feed and the
 * consent signature feed — were plain `new WebSocket(...)` with an `onmessage`
 * handler and nothing else. No `onclose`, so a dropped connection was never
 * re-established. The page kept working; it just silently stopped updating.
 *
 * That was survivable when one process served everything: the socket only died
 * if the server restarted, and the user reloaded anyway. Behind the load
 * balancer it dies every time a VM is drained — which is the entire point of
 * the migration. Without this, draining a machine during working hours leaves
 * every affected user looking at a worklist that has quietly stopped moving,
 * with no error and nothing in any log.
 *
 * So this is not a nicety. Until the client reconnects, a drain cannot be
 * proven safe no matter what the server does.
 *
 * WHAT IT HANDLES
 *
 *   server went away      a drain, a deploy, a rolling restart
 *   network blipped       wifi, VPN
 *   laptop slept          the socket is dead but no close event ever fires,
 *                         so waking the tab has to trigger a check
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 *
 *   It does not queue or replay messages. Every message these sockets carry is
 *   a "something changed, go and look" nudge with no payload state, so a missed
 *   one costs nothing once the next arrives — and the caller re-reads on
 *   reconnect anyway. Buffering would add a delivery guarantee nothing needs.
 */

export type SocketState = 'connecting' | 'open' | 'closed';

export class ReconnectingSocket {
    private ws: WebSocket | null = null;
    private stopped = false;
    private attempt = 0;
    private timer: any = null;
    private readonly onVisible: () => void;
    private readonly onOnline: () => void;

    /** Backoff, capped. The cap matters: a VM drain takes ~25s and a rolling
     *  restart of two processes a little over a minute, so retries must stay
     *  frequent enough to reconnect promptly without a thundering herd when a
     *  whole environment bounces at once. */
    private static readonly BASE_MS = 500;
    private static readonly MAX_MS = 10000;

    /**
     * @param url       Recomputed on every attempt, not captured once — the
     *                  worklist socket embeds a clinic id that can change while
     *                  the page is open, and a stale url would silently
     *                  reconnect to the wrong room.
     * @param onMessage Called for each message.
     * @param onOpen    Called after every successful (re)connect, including the
     *                  first. This is where a caller should re-read whatever it
     *                  might have missed while disconnected.
     */
    constructor(
        private readonly url: () => string,
        private readonly onMessage: (ev: MessageEvent) => void,
        private readonly label = 'socket',
        private readonly onOpen?: () => void,
    ) {
        // A sleeping laptop's socket is dead, but no close event is delivered
        // until the OS notices, which can be minutes. Checking when the tab
        // becomes visible again is what makes waking from sleep recover
        // promptly rather than eventually.
        this.onVisible = () => {
            if (document.visibilityState === 'visible') this.ensureConnected();
        };
        this.onOnline = () => this.ensureConnected();
        document.addEventListener('visibilitychange', this.onVisible);
        window.addEventListener('online', this.onOnline);

        this.connect();
    }

    get state(): SocketState {
        if (this.stopped) return 'closed';
        if (this.ws && this.ws.readyState === WebSocket.OPEN) return 'open';
        return 'connecting';
    }

    get isOpen(): boolean {
        return !!this.ws && this.ws.readyState === WebSocket.OPEN;
    }

    send(data: string): boolean {
        if (!this.isOpen) return false;
        this.ws!.send(data);
        return true;
    }

    /** Deliberate close. Stops reconnecting — this is how a caller says "done",
     *  and it must be distinguishable from the connection dropping. */
    close(): void {
        this.stopped = true;
        this.clearTimer();
        document.removeEventListener('visibilitychange', this.onVisible);
        window.removeEventListener('online', this.onOnline);
        if (this.ws) {
            // Drop the handlers first: closing fires onclose, which would
            // otherwise schedule a reconnect for a socket we are discarding.
            this.ws.onopen = this.ws.onmessage = this.ws.onerror = this.ws.onclose = null;
            try { this.ws.close(1000, 'client done'); } catch { /* already gone */ }
            this.ws = null;
        }
    }

    private ensureConnected(): void {
        if (this.stopped) return;
        if (this.ws && (this.ws.readyState === WebSocket.OPEN ||
                        this.ws.readyState === WebSocket.CONNECTING)) return;
        this.clearTimer();
        this.attempt = 0;   // a user-visible event is not a failure — retry at once
        this.connect();
    }

    private connect(): void {
        if (this.stopped) return;
        let target: string;
        try {
            target = this.url();
        } catch (e) {
            this.scheduleReconnect();
            return;
        }

        try {
            this.ws = new WebSocket(target);
        } catch (e) {
            // Construction throws on a malformed URL; treat it as a failed
            // attempt rather than letting it escape into the caller.
            this.scheduleReconnect();
            return;
        }

        this.ws.onopen = () => {
            this.attempt = 0;
            if (this.onOpen) this.onOpen();
        };

        this.ws.onmessage = (ev) => this.onMessage(ev);

        // onerror is always followed by onclose, so reconnecting is handled
        // there — doing it in both schedules two attempts for one failure.
        this.ws.onerror = () => { /* handled by onclose */ };

        this.ws.onclose = () => {
            this.ws = null;
            if (!this.stopped) this.scheduleReconnect();
        };
    }

    private scheduleReconnect(): void {
        if (this.stopped || this.timer) return;
        const backoff = Math.min(
            ReconnectingSocket.MAX_MS,
            ReconnectingSocket.BASE_MS * Math.pow(2, this.attempt),
        );
        // Jitter, because a drain drops every client at the same instant and
        // they would otherwise all retry in lockstep against the one remaining
        // process.
        const delay = backoff / 2 + Math.random() * (backoff / 2);
        this.attempt++;
        this.timer = setTimeout(() => {
            this.timer = null;
            this.connect();
        }, delay);
    }

    private clearTimer(): void {
        if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    }
}

/** The ws:// or wss:// origin for this page. Same host and port the app was
 *  served from, so it follows the balancer without configuration. */
export function socketOrigin(): string {
    return (window.location.protocol === 'https:' ? 'wss://' : 'ws://') +
        window.location.hostname +
        (window.location.port ? ':' + window.location.port : '');
}
